import { notifyFileWriteJournalChanged, subscribeFileWriteJournalChanged } from "@/lib/fileWriteJournalBus";
import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { connectedFileReadSessionId, type FileReadIdentity } from "@/lib/connectedFileRead";
import { claimConnectorWrite, finishConnectorWrite, loadConnectorWrite, type ConnectorWriteBody, type PendingConnectorWrite } from "@/lib/connectorPendingWrite";
import { FILE_WRITE_CONTRACT, fileCommitBody, fileJournalBody, fileOwnerSession, fileReceiptBody, invokeFileWrite,
  validateFileWriteJournal, validateFileWriteReply, validatePreparedFileWrite, verifyPreparedFileContent, verifyFileUploadBytes, type FileWriteJournalBody, type PreparedFileWrite } from "@/lib/fileExecutor";

export function useFileConnectorWrite(ownerId: string, actionId: string, mirrorSessionId: string | undefined,
  surfaceQuota: (error: unknown) => Promise<boolean>, originCurrent?: () => boolean, enabled = true, expectedAuthSessionId?: string) {
  const [pending, setPending] = useState<PendingConnectorWrite | null>(null), [review, setReview] = useState<PreparedFileWrite | null>(null);
  const [ready, setReady] = useState(false), [busy, setBusy] = useState(false), [error, setError] = useState<string | null>(null);
  const generation = useRef(0), lock = useRef(false), controller = useRef<AbortController | null>(null), sid = useRef<string | null>(null);
  const preparedBody = useRef<FileWriteJournalBody | null>(null), preparedUpload = useRef<string | null>(null), activeOrigin = useRef(originCurrent);
  activeOrigin.current = originCurrent;
  const current = useCallback((epoch: number) => generation.current === epoch && (activeOrigin.current?.() ?? true), []);
  const belongsToCurrent = (entry: PendingConnectorWrite) => entry.body.idempotency_key === (mirrorSessionId
    ? "mirror_file_" + actionId + "_" + entry.body.action : "file:" + actionId + ":" + entry.body.action) &&
    (mirrorSessionId ? (entry.body.mirror_context as { session_id?: string } | undefined)?.session_id === mirrorSessionId : entry.body.mirror_context === undefined);
  useEffect(() => {
    const epoch = ++generation.current;
    sid.current = null; lock.current = false; preparedBody.current = null; preparedUpload.current = null; controller.current?.abort();
    setBusy(false); setReady(false); setPending(null); setReview(null); setError(null);
    if (!enabled) { const inactiveGeneration = generation; setReady(true); return () => { inactiveGeneration.current++; }; }
    void (async () => {
      const session = await fileOwnerSession(ownerId, expectedAuthSessionId);
      if (!current(epoch)) return;
      sid.current = session.sid;
      const entry = await loadConnectorWrite(ownerId, "file");
      await fileOwnerSession(ownerId, session.sid);
      if (!current(epoch)) return;
      if (entry && (!validateFileWriteJournal(entry, ownerId) || entry.body.auth_session_id !== session.sid)) throw new Error("prior_file_action");
      // A stored succeeded flag has no authority after a reload.
      setPending(entry ? { ...entry, status: "pending", confirmedResult: undefined } : null);
      setReady(true);
    })().catch(cause => { if (current(epoch)) setError(cause instanceof Error ? cause.message : "pending_storage_unavailable"); });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const nextSid = session?.access_token ? connectedFileReadSessionId(session.access_token) : null;
      if (session?.user.id === ownerId && (sid.current === null || nextSid === sid.current)) return;
      generation.current++; lock.current = true; controller.current?.abort(); preparedBody.current = null; preparedUpload.current = null;
      setReady(false); setBusy(false); setReview(null); setPending(null); setError("invalid_session");
    });
    const lifecycle = generation, activeController = controller;
    return () => { subscription.unsubscribe(); lifecycle.current++; activeController.current?.abort(); preparedUpload.current = null; };
  }, [ownerId, actionId, mirrorSessionId, current, enabled, expectedAuthSessionId]);
  useEffect(() => {
    const epoch = generation.current;
    return subscribeFileWriteJournalChanged(changedOwner => {
      if (changedOwner !== ownerId || !current(epoch) || lock.current || !sid.current) return;
      void (async () => {
        await fileOwnerSession(ownerId, sid.current!);
        const stored = await loadConnectorWrite(ownerId, "file");
        if (current(epoch) && !stored) { setPending(null); setReview(null); preparedBody.current = null; preparedUpload.current = null; setError(null); }
      })().catch(() => { if (current(epoch)) setError("invalid_session"); });
    });
  }, [ownerId, actionId, mirrorSessionId, current]);
  const captureCurrent = () => { const epoch = generation.current; return () => ready && current(epoch); };
  const transport = async (body: Record<string, unknown>, epoch: number) => {
    if (!sid.current || !current(epoch)) throw new Error("invalid_session");
    const aborter = new AbortController(); controller.current = aborter;
    const timeout = setTimeout(() => aborter.abort(), 60_000);
    try { return await invokeFileWrite(ownerId, sid.current, body, { signal: aborter.signal, isCurrent: () => current(epoch), surfaceQuota }); }
    finally { clearTimeout(timeout); }
  };
  const prepare = async (create: () => Promise<ConnectorWriteBody> | ConnectorWriteBody, sourceIdentity?: FileReadIdentity) => {
    if (lock.current || !ready || pending) return null;
    const epoch = generation.current; lock.current = true; setBusy(true); setError(null); preparedUpload.current = null;
    try {
      const payload = await create(); if (!current(epoch) || !sid.current) throw new Error("invalid_session");
      const request = { ...payload, write_contract: FILE_WRITE_CONTRACT, phase: "prepare", expected_owner_id: ownerId };
      const response = await transport(request, epoch);
      if (!validatePreparedFileWrite(response, payload, ownerId, sid.current) || !await verifyPreparedFileContent(response, payload)) throw new Error("outcome_unknown");
      if (sourceIdentity && (response.auth_session_id !== sourceIdentity.auth_session_id || response.connection_id !== sourceIdentity.connection_id)) throw new Error("connection_changed");
      if (!current(epoch)) return null;
      preparedBody.current = fileJournalBody(payload, response);
      preparedUpload.current = payload.action === "upload_file" ? String(payload.base64) : null; setReview(response);
      return response;
    } catch (cause) { if (current(epoch)) { preparedUpload.current = null; setError(cause instanceof Error ? cause.message : "outcome_unknown"); } return null; }
    finally { if (current(epoch)) { lock.current = false; setBusy(false); } }
  };
  const recover = async () => {
    if (lock.current || !ready || !pending || !validateFileWriteJournal(pending, ownerId)) return null;
    const epoch = generation.current; lock.current = true; setBusy(true); setError(null);
    try {
      const response = await transport(fileReceiptBody(pending.body), epoch);
      if (!validateFileWriteReply(response, pending.body, "receipt_status")) throw new Error("outcome_unknown");
      if (response.status === "failed" && response.safe_retry === true) {
        await finishConnectorWrite(pending, true, () => { if (!current(epoch)) throw new Error("invalid_session"); });
        if (current(epoch)) { setPending(null); setReview(null); preparedBody.current = null; preparedUpload.current = null; setError("file_safe_retry"); notifyFileWriteJournalChanged(ownerId); }
        return null;
      }
      if (response.status !== "succeeded") throw new Error("outcome_unknown");
      await finishConnectorWrite(pending, false, () => { if (!current(epoch)) throw new Error("invalid_session"); });
      if (!current(epoch)) return null;
      const completed = { ...pending, status: "succeeded" as const, confirmedResult: response };
      setPending(completed); return completed;
    } catch (cause) { if (current(epoch)) setError(cause instanceof Error ? cause.message : "outcome_unknown"); return null; }
    finally { if (current(epoch)) { lock.current = false; setBusy(false); } }
  };
  const execute = async () => {
    if (pending) return recover();
    if (lock.current || !ready || !preparedBody.current || !review) return null;
    const epoch = generation.current, body = structuredClone(preparedBody.current), upload = preparedUpload.current;
    lock.current = true; setBusy(true); setError(null);
    try {
      if (Date.parse(body.expires_at) <= Date.now()) throw new Error("review_expired");
      if (!await verifyFileUploadBytes(body, upload)) throw new Error("review_changed");
      await fileOwnerSession(ownerId, body.auth_session_id);
      if (!current(epoch)) throw new Error("invalid_session");
      const claimed = await claimConnectorWrite(ownerId, "file", body, { title: body.review.target.name || body.review.name }, { authSessionId: body.auth_session_id });
      if (!current(epoch)) throw new Error("invalid_session");
      if (!claimed.created || JSON.stringify(claimed.entry.body) !== JSON.stringify(body)) { setPending(claimed.entry); throw new Error("prior_file_action"); }
      // Claim durably before any provider mutation; every later failure recovers only by lookup.
      setPending(claimed.entry); notifyFileWriteJournalChanged(ownerId);
      preparedUpload.current = null;
      const response = await transport(fileCommitBody(body, upload ?? undefined), epoch);
      if (!validateFileWriteReply(response, body, "commit") || response.status !== "succeeded") throw new Error("outcome_unknown");
      await finishConnectorWrite(claimed.entry, false, () => { if (!current(epoch)) throw new Error("invalid_session"); });
      if (!current(epoch)) return null;
      const completed = { ...claimed.entry, status: "succeeded" as const, confirmedResult: response };
      setPending(completed); setReview(null); return completed;
    } catch (cause) {
      if (current(epoch)) {
        const code = cause instanceof Error ? cause.message : "outcome_unknown";
        setError(code);
        if (code === "review_expired" || code === "review_changed") { setReview(null); preparedBody.current = null; preparedUpload.current = null; }
      }
      return null;
    } finally { if (current(epoch)) { lock.current = false; setBusy(false); } }
  };
  const discardReview = () => { if (busy || pending) return; preparedBody.current = null; preparedUpload.current = null; setReview(null); setError(null); };
  const acknowledge = async () => {
    if (!pending || pending.status !== "succeeded" || !validateFileWriteJournal(pending, ownerId) ||
      !validateFileWriteReply(pending.confirmedResult, pending.body, (pending.confirmedResult as { phase?: string })?.phase === "commit" ? "commit" : "receipt_status")) return false;
    const epoch = generation.current;
    try {
      await fileOwnerSession(ownerId, pending.body.auth_session_id);
      if (!current(epoch)) throw new Error("invalid_session");
      await finishConnectorWrite(pending, true, () => { if (!current(epoch)) throw new Error("invalid_session"); });
      if (!current(epoch)) return false;
      setPending(null); setReview(null); preparedBody.current = null; preparedUpload.current = null; notifyFileWriteJournalChanged(ownerId); return true;
    } catch (cause) { if (current(epoch)) setError(cause instanceof Error ? cause.message : "pending_storage_unavailable"); return false; }
  };
  return { pending, review, ready, busy, error, prepare, execute, recover, acknowledge, discardReview, captureCurrent,
    prior: !!pending && !belongsToCurrent(pending), belongsToCurrent };
}
